import type { TrafficSourceConfig } from './traffic-sources.service';
import api, { ApiResponse, QueryPayload, QueryResponse, WhereCondition } from './api';

// ============================================
// Interfaces
// ============================================

export interface FacebookAdAccount {
  id: string;
  name: string;
  timezone: string;
}

export interface TSIntegrationConfig {
  credentials: Record<string, any>;
  sync_cost?: {
    enabled: boolean;
  };
  balance?: { enabled: boolean };
  campaign_group_ids?: string[];
}

export interface TaboolaCampaignGroup { id: string; name: string; status?: string }

export interface TSIntegrationInfo {
  sync_cost?: {
    last_run?: string;
    done_until?: string;
    status_code?: number | string;
    message?: string;
  };
  balance?: {
    balance?: number;
    last_run?: string;
  };
}

export interface TSIntegration {
  id: string;
  workspace_id: string;
  traffic_source_id: string;
  kind: string;
  config: TSIntegrationConfig;
  info: TSIntegrationInfo;
  enabled: boolean;
  status: string;
  created_at: string;
  updated_at: string;
  traffic_source?: { id: string; name: string; config: TrafficSourceConfig };
}

export interface CostSyncResult {
  synced_campaigns: number;
  total_cost: number;
  days_processed: number;
}

// ============================================
// Service
// ============================================

export const tsIntegrationsService = {
  query: async (filters: { limit?: number; offset?: number; where?: WhereCondition } = {}): Promise<{ data: TSIntegration[]; total: number }> => {
    const payload: QueryPayload = {
      offset: filters.offset ?? 0,
      limit: filters.limit ?? 100,
      order_by: 'ts_integration.created_at',
      order: 'desc',
      ...(filters.where && { where: filters.where }),
    };
    const response = await api.post<QueryResponse<TSIntegration>>('/ts-integrations/query', payload);
    return { data: response.data.data, total: response.data._meta?.total ?? 0 };
  },

  getById: async (id: string): Promise<TSIntegration> => {
    const response = await api.get<ApiResponse<TSIntegration>>(`/ts-integrations/${id}`);
    return response.data.data!;
  },

  create: async (data: Partial<TSIntegration>): Promise<TSIntegration> => {
    const response = await api.post<ApiResponse<TSIntegration>>('/ts-integrations', data);
    return response.data.data!;
  },

  update: async (id: string, data: Partial<TSIntegration>): Promise<TSIntegration> => {
    const response = await api.patch<ApiResponse<TSIntegration>>(`/ts-integrations/${id}`, data);
    return response.data.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/ts-integrations/${id}`);
  },

  syncCost: async (id: string): Promise<CostSyncResult> => {
    const response = await api.post<ApiResponse<CostSyncResult>>(`/ts-integrations/${id}/sync-cost`);
    return response.data.data!;
  },

  fetchFbAdAccounts: async (accessToken: string): Promise<FacebookAdAccount[]> => {
    const response = await api.post<ApiResponse<FacebookAdAccount[]>>('/ts-integrations/fetch-fb-accounts', {
      access_token: accessToken,
    });
    return response.data.data!;
  },

  fetchTaboolaCampaignGroups: async (creds: { client_id: string; client_secret: string; account_id: string }): Promise<TaboolaCampaignGroup[]> => {
    const response = await api.post<ApiResponse<TaboolaCampaignGroup[]>>('/ts-integrations/fetch-taboola-campaign-groups', creds);
    return response.data.data!;
  },
};
